import { useEffect, useState } from "react";
import { Button, Sheet, TextArea } from "@components/ui";
import type { Call, Disposition } from "@aria/shared";
import { apiClient } from "@lib/apiClient";
import { signMedia } from "@lib/media";
import styles from "./CallDispositionModal.module.css";

export interface CallDispositionModalProps {
  call: Call | null;
  onClose: () => void;
  onSaved?: (callId: string) => void;
}

export function CallDispositionModal({ call, onClose, onSaved }: CallDispositionModalProps) {
  const [dispositions, setDispositions] = useState<Disposition[]>([]);
  const [dispositionId, setDispositionId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingFailed, setRecordingFailed] = useState(false);

  useEffect(() => {
    if (!call) return;
    apiClient
      .get<Disposition[]>("/dispositions")
      .then((list) => {
        setDispositions(list);
        const initial = list.find((d) => d.isDefault) ?? list[0];
        setDispositionId(initial?.id ?? "");
      })
      .catch(() => setDispositions([]));
    setNotes("");
  }, [call]);

  // Lazily mint a signed URL for the call's recording when the modal opens for
  // a call that has one. The signed URL works directly as an <audio src>.
  useEffect(() => {
    setRecordingUrl(null);
    setRecordingFailed(false);
    if (!call?.recordingUrl) return;
    const callId = call.id;
    let active = true;
    signMedia({ kind: "recording", callId })
      .then((url) => {
        if (active) setRecordingUrl(url);
      })
      .catch(() => {
        if (active) setRecordingFailed(true);
      });
    return () => {
      active = false;
    };
  }, [call]);

  const save = async () => {
    if (!call || !dispositionId) return;
    setSaving(true);
    try {
      await apiClient.post(`/dispositions/calls/${call.id}/disposition`, {
        dispositionId,
        notes,
      });
      onSaved?.(call.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const party = call ? (call.direction === "INBOUND" ? call.from : call.to) : "";
  const duration = call?.durationSec ?? 0;

  return (
    <Sheet
      open={Boolean(call)}
      onClose={onClose}
      title="Call disposition"
      footer={
        <>
          <Button variant="gray" onClick={onClose}>
            Skip
          </Button>
          <Button onClick={() => void save()} isLoading={saving} disabled={!dispositionId}>
            Save
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.summary}>
          <div className={styles.party}>{party}</div>
          <div className={styles.meta}>
            {call?.direction === "INBOUND" ? "Incoming" : "Outgoing"} ·{" "}
            {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, "0")}
          </div>
        </div>
        {call?.recordingUrl && !recordingFailed && (
          <div className={styles.field}>
            <label className={styles.label}>Recording</label>
            {recordingUrl ? (
              <audio
                controls
                preload="none"
                src={recordingUrl}
                className={styles.audio}
                onError={() => setRecordingFailed(true)}
              >
                Your browser does not support audio playback.
              </audio>
            ) : (
              <div className={styles.audioLoading}>Loading recording…</div>
            )}
          </div>
        )}
        <div className={styles.field}>
          <label className={styles.label}>Disposition</label>
          <select
            className={styles.select}
            value={dispositionId}
            onChange={(e) => setDispositionId(e.target.value)}
          >
            {dispositions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        <TextArea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Optional notes…"
        />
      </div>
    </Sheet>
  );
}